/**
 * Shell Registry — cross-shell URL resolution for DeepLink component.
 *
 * Hardcoded table matching `frontend/config/registries/react_apps.yaml`.
 * A unit test (`__tests__/shellRegistry.test.ts`) asserts every entry here
 * matches the corresponding YAML entry — fail that test when you update the
 * YAML, then update this file to match.
 *
 * @package @plerity/dashboard-shell
 */

// ---------------------------------------------------------------------------
// Shell identifiers
// ---------------------------------------------------------------------------

/**
 * All shell identifiers that DeepLink can navigate to. Defined here — in the
 * registry keyed by it — rather than in DeepLink.tsx, so shellRegistry.ts stays
 * a leaf module (no back-edge to DeepLink.tsx). DeepLink.tsx re-exports it, so
 * the public `@plerity/dashboard-shell` barrel export and every
 * `from ".../DeepLink"` import path are unchanged. Extend this union +
 * SHELL_REGISTRY together when adding a new shell.
 */
export type DeepLinkShell = "crm" | "ops" | "finance" | "ai" | "platform" | "design" | "calls";

// ---------------------------------------------------------------------------
// Registry entry type
// ---------------------------------------------------------------------------

export interface ShellRegistryEntry {
  /** Production hostname, e.g. "crm.animasmarketing.com" */
  productionDomain: string;
  /** Localhost port for dev mode, e.g. 3051 */
  devPort: number;
  /** Preview hostname for preview deploys, e.g. "preview-crm.animasmarketing.com" */
  previewDomain: string;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/**
 * Hardcoded shell registry.
 *
 * Source of truth: `frontend/config/registries/react_apps.yaml` (the `domain`
 * and `dev_port` fields). The `shellRegistry.test.ts` parity test asserts
 * these match. Update both files together.
 */
export const SHELL_REGISTRY: Record<DeepLinkShell, ShellRegistryEntry> = {
  // CRM was consolidated into the Ops shell (2026-05-26, ops_crm_subdomain
  // consolidation). It is no longer a standalone app — its routes live in the
  // ops shell under `/crm/*`. The legacy `crm.animasmarketing.com` domain is a
  // 301 redirect that *prepends* `/crm`, so a `/crm/...`-prefixed deep-link
  // would double-prefix (→ ops.animasmarketing.com/crm/crm/... → 404). Resolve
  // crm deep-links directly to the ops infrastructure, which serves CRM at
  // `/crm/*`. The crm entry therefore mirrors the ops entry below and is parity-
  // checked against the `ops` row in react_apps.yaml (crm has no own row).
  crm: {
    productionDomain: "ops.animasmarketing.com",
    devPort: 3057,
    previewDomain: "preview-ops.animasmarketing.com",
  },
  ops: {
    productionDomain: "ops.animasmarketing.com",
    devPort: 3057,
    previewDomain: "preview-ops.animasmarketing.com",
  },
  finance: {
    productionDomain: "finance.animasmarketing.com",
    devPort: 3050,
    previewDomain: "preview-finance.animasmarketing.com",
  },
  ai: {
    productionDomain: "ai.animasmarketing.com",
    devPort: 3053,
    previewDomain: "preview-ai.animasmarketing.com",
  },
  platform: {
    productionDomain: "platform.animasmarketing.com",
    devPort: 3054,
    previewDomain: "preview-platform.animasmarketing.com",
  },
  design: {
    productionDomain: "design.animasmarketing.com",
    devPort: 3022,
    previewDomain: "preview-design.animasmarketing.com",
  },
  calls: {
    productionDomain: "calls.animasmarketing.com",
    devPort: 3110,
    previewDomain: "preview-calls.animasmarketing.com",
  },
};

// ---------------------------------------------------------------------------
// URL resolution
// ---------------------------------------------------------------------------

export interface ShellUrlContext {
  hostname: string;
}

/**
 * Resolve the full URL for a deep-link target given the current hostname.
 *
 * Resolution rules:
 * - `localhost`, `127.0.0.1`, `*.local` → `http://localhost:{devPort}{path}`
 * - `preview-*.animasmarketing.com` or `preview.*.pages.dev` → uses previewDomain
 * - All other production hostnames → uses productionDomain
 *
 * Cookie domain `.animasmarketing.com` is shared across all production and
 * preview-custom-domain deployments — no auth handoff needed.
 */
export function resolveShellUrl(kind: DeepLinkShell, path: string, ctx: ShellUrlContext): string {
  const entry = SHELL_REGISTRY[kind];
  const hostname = ctx.hostname.toLowerCase();

  // Dev: localhost, 127.0.0.1, *.local
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".local")) {
    return `http://localhost:${entry.devPort}${path}`;
  }

  // Preview: `preview-{app}.animasmarketing.com` or `preview.{project}.pages.dev`
  if (hostname.startsWith("preview-") || hostname.startsWith("preview.")) {
    return `https://${entry.previewDomain}${path}`;
  }

  // Production
  return `https://${entry.productionDomain}${path}`;
}
